/* eslint-disable react/prop-types */
import React from 'react';
import Barcode from 'react-barcode';
import { barcodeValue, documentCodeValue } from 'utils/printBarcodes';

function BarcodeItem({ label, value, labelColor, height, fontSize, labelFontSize, gridArea }) {
  if (!value) return null;
  return (
    <div style={gridArea ? { gridArea } : undefined}>
      <div style={{ fontSize: labelFontSize, color: labelColor, fontWeight: 700, textTransform: 'uppercase', lineHeight: 1.2 }}>
        {label}
      </div>
      <Barcode value={value} format="CODE128" width={1} height={height} fontSize={fontSize} margin={1} displayValue />
    </div>
  );
}

/**
 * Vertically-stacked "Patient ID / Couple ID / Spouse ID [/ Document No]"
 * barcode block for react-to-print documents (clinical/IVF prints,
 * Prescription, Infertility Summary) — the live-DOM counterpart to
 * utils/printBarcodes.js's codeBarcodeStack(), which serves the
 * window.open()+document.write() based prints instead. Renders nothing if
 * no codes are passed.
 *
 * `documentCode`/`documentLabel`: the print document's own number (bill_no,
 * procedure external_code, prescription_code, etc.) — uses documentCodeValue
 * (strip-to-after-last-dash), not the patient/couple/spouse 12-digit rule.
 * `mono` switches the labels to black — for the pure black & white print
 * templates (e.g. ProcedureInitiationPrint) that never use brand color.
 * `size` bumps up the barcode/label dimensions — 'normal' (default, used by
 * the denser IVF/procedure sheets) or 'large' (Infertility Summary, which
 * has more header room and wanted a more legible barcode).
 * `layout` — 'column' (default) stacks every code in one vertical list;
 * 'grid2' arranges them in a 2-column grid — Patient ID and Couple ID side
 * by side on top, Spouse ID directly under Patient ID (Infertility
 * Summary's barcode panel); 'row' puts every code side by side in a single
 * horizontal line (IVF Lab Performance print).
 */
export default function PatientCodeBarcodes({ patientCode, coupleCode, spouseCode, documentCode, documentLabel, mono, size = 'normal', layout = 'column', style }) {
  if (!patientCode && !coupleCode && !spouseCode && !documentCode) return null;
  const labelColor = mono ? '#000' : '#00798E';
  const dims = size === 'large'
    ? { height: 26, fontSize: 8, labelFontSize: '6.5pt' }
    : { height: 14, fontSize: 5, labelFontSize: '5pt' };
  if (layout === 'row') {
    return (
      <div style={{ display: 'flex', flexDirection: 'row', gap: 10, alignItems: 'flex-start', ...style }}>
        <BarcodeItem label="Patient ID" value={patientCode && (barcodeValue(patientCode) || patientCode)} labelColor={labelColor} {...dims} />
        <BarcodeItem label="Couple ID" value={coupleCode && (barcodeValue(coupleCode) || coupleCode)} labelColor={labelColor} {...dims} />
        <BarcodeItem label="Spouse ID" value={spouseCode && (barcodeValue(spouseCode) || spouseCode)} labelColor={labelColor} {...dims} />
        <BarcodeItem label={documentLabel || 'Doc No'} value={documentCode && documentCodeValue(documentCode)} labelColor={labelColor} {...dims} />
      </div>
    );
  }
  if (layout === 'grid2') {
    // Explicit placement (not row-major auto-flow) so a missing code
    // doesn't shift the others into the wrong cell — Patient/Couple fixed
    // on top, Spouse fixed directly under Patient regardless of which
    // codes are actually present.
    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gridTemplateRows: 'auto auto', gap: 6, ...style }}>
        <BarcodeItem label="Patient ID" value={patientCode && (barcodeValue(patientCode) || patientCode)} labelColor={labelColor} gridArea="1 / 1" {...dims} />
        <BarcodeItem label="Couple ID" value={coupleCode && (barcodeValue(coupleCode) || coupleCode)} labelColor={labelColor} gridArea="1 / 2" {...dims} />
        <BarcodeItem label="Spouse ID" value={spouseCode && (barcodeValue(spouseCode) || spouseCode)} labelColor={labelColor} gridArea="2 / 1" {...dims} />
        <BarcodeItem label={documentLabel || 'Doc No'} value={documentCode && documentCodeValue(documentCode)} labelColor={labelColor} gridArea="2 / 2" {...dims} />
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: size === 'large' ? 4 : 2, alignItems: 'flex-start', ...style }}>
      <BarcodeItem label="Patient ID" value={patientCode && (barcodeValue(patientCode) || patientCode)} labelColor={labelColor} {...dims} />
      <BarcodeItem label="Couple ID" value={coupleCode && (barcodeValue(coupleCode) || coupleCode)} labelColor={labelColor} {...dims} />
      <BarcodeItem label="Spouse ID" value={spouseCode && (barcodeValue(spouseCode) || spouseCode)} labelColor={labelColor} {...dims} />
      <BarcodeItem label={documentLabel || 'Doc No'} value={documentCode && documentCodeValue(documentCode)} labelColor={labelColor} {...dims} />
    </div>
  );
}
